import { useState, useCallback, useRef, useEffect, useMemo } from "react";
import { httpService } from "../../../../services/httpService";
import { toast } from "react-toastify";
import { tokenDecode } from "../../../../utils/helper";
import Select from "react-select";
import { GoPeople } from "react-icons/go";
import {
  FiUser,
  FiPhone,
  FiMail,
  FiMapPin,
  FiFileText,
  FiTag,
  FiSave,
  FiX,
  FiUserPlus,
  FiEdit3,
  FiHome,
  FiMessageSquare,
  FiChevronLeft,
  FiChevronRight,
  FiExternalLink,
} from "react-icons/fi";

const isPetScopeField = (mapsTo = "", sectionKey = "", sectionName = "") => {
  const m = String(mapsTo || "").toLowerCase();
  const sk = String(sectionKey || "").toLowerCase();
  const sn = String(sectionName || "").toLowerCase();

  if (m.startsWith("pet.") || m.startsWith("boarding.")) return true;
  if (
    m.includes("vaccin") ||
    m.includes("medical") ||
    m.includes("medicat") ||
    m.includes("diet") ||
    m.includes("feeding")
  ) {
    return true;
  }

  const petIndicators = [
    "sec_pet",
    "pet_basic",
    "pet_info",
    "health",
    "medical",
    "medicat",
    "vaccin",
    "diet",
    "feeding",
    "meal",
    "behaviour",
    "behavior",
    "appearance",
    "hair",
  ];

  for (const ind of petIndicators) {
    if (sk.includes(ind) || sn.includes(ind)) {
      if (
        (sk.includes("customer") ||
          sk.includes("client") ||
          sk.includes("parent") ||
          sn.includes("customer") ||
          sn.includes("client") ||
          sn.includes("parent")) &&
        (ind === "pet" || ind === "pet_basic" || ind === "sec_pet")
      ) {
        continue;
      }
      return true;
    }
  }

  if (
    (sn.includes("pet") || sk.includes("pet")) &&
    !sn.includes("parent") &&
    !sn.includes("customer") &&
    !sn.includes("client") &&
    !sk.includes("parent") &&
    !sk.includes("customer") &&
    !sk.includes("client")
  ) {
    return true;
  }

  return false;
};

const AddNewClientAppointment = ({
  onClose,
  phone,
  onSave,
  onSaveAndAddPet,
  clientId = null,
  isOpen,
}) => {
  const decoded = tokenDecode();
  const { data } = decoded || {};
  const vendorId = data?.user?.vendor_id;

  const [clientForm, setClientForm] = useState({
    firstname: "",
    lastname: "",
    nickname: "",
    phone: phone || "",
    whatsapp_number: phone || "",
    alternate_phone: "",
    email: "",
    address: {
      address1: "",
      address2: "",
      landmark: "",
      city: "",
      state: "",
      zip: "",
    },
    vendor_id: vendorId,
    labels: "",
    client_notes: "",
    reference_source: "",
    id_proof: null,
    documents: [],
    custom_fields: {},
  });

  const [isIdModalOpen, setIsIdModalOpen] = useState(false);
  const [selectedIdIndex, setSelectedIdIndex] = useState(0);

  const [formConfig, setFormConfig] = useState({
    labelMap: {},
    customerCustomFields: [],
    addressCustomFields: [],
    otherCustomSections: [],
  });

  const fetchFormConfig = useCallback(async () => {
    try {
      const response = await httpService.get("/vendors/getAllForms");
      const forms = response?.data?.data || response?.data || [];
      if (!Array.isArray(forms) || forms.length === 0) return;

      let targetForm = null;
      if (clientForm.intake_form_id) {
        targetForm = forms.find(
          (f) =>
            String(f.form_id || f._id) === String(clientForm.intake_form_id),
        );
      }
      if (!targetForm) {
        targetForm =
          forms.find((f) => {
            const name = String(f.form_name || f.formName || "").toLowerCase();
            return (
              name.includes("onboarding") ||
              name.includes("customer") ||
              name.includes("client")
            );
          }) || forms[0];
      }

      const labelMap = {};
      const customerCustom = [];
      const addressCustom = [];
      const otherSectionsMap = new Map();

      const coreCustomerKeys = new Set([
        "customer.firstname",
        "customer.lastname",
        "customer.phone",
        "customer.whatsapp_number",
        "customer.alternate_phone",
        "customer.email",
        "customer.reference_source",
        "customer.notes",
        "customer.client_notes",
        "customer.signature",
        "customer.id_proof",
        "customer.documents",
        "customer.services_interested_in",
      ]);

      const coreAddressKeys = new Set([
        "customer.address.address1",
        "customer.address.address2",
        "customer.address.street",
        "customer.address.landmark",
        "customer.address.city",
        "customer.address.state",
        "customer.address.zip",
        "customer.address.maps_url",
      ]);

      const allQuestions = [];
      const seenQ = new Set();
      const addQuestion = (q) => {
        const idKey = String(q.field_uid || q._id || q.question);
        if (!seenQ.has(idKey)) {
          seenQ.add(idKey);
          allQuestions.push(q);
        }
      };

      if (targetForm && Array.isArray(targetForm.questions)) {
        targetForm.questions.forEach(addQuestion);
      }
      forms.forEach((f) => {
        const name = String(f.form_name || f.formName || "").toLowerCase();
        if (
          name.includes("onboarding") ||
          name.includes("customer") ||
          name.includes("client")
        ) {
          if (Array.isArray(f.questions)) {
            f.questions.forEach(addQuestion);
          }
        }
      });

      allQuestions.forEach((q) => {
        if (q.enabled === false) return;
        const answerType = String(q.answerType || "").toLowerCase();
        if (answerType === "section_heading" || answerType === "section_header")
          return;

        const mapsTo = String(q.maps_to || q.mapsTo || "custom").trim();
        const label = String(q.question || q.label || "").trim();

        if (coreCustomerKeys.has(mapsTo) || coreAddressKeys.has(mapsTo)) {
          if (label) {
            labelMap[mapsTo] = label;
          }
          return;
        }

        const sectionKey = String(
          q.section_key || q.section || "",
        ).toLowerCase();
        const sectionName = String(
          q.sectionName || q.section_name || "",
        ).toLowerCase();

        // Strict filter: Exclude all Pet questions/sections from the Client Drawer
        if (isPetScopeField(mapsTo, sectionKey, sectionName)) return;

        const customFieldObj = {
          _id: q._id,
          field_uid: q.field_uid || q.fieldUid || q._id,
          label: label || "Custom Field",
          answerType: q.answerType || "text",
          options: q.options || [],
          isRequired: q.isRequired || false,
          placeholder: q.placeholder || "",
        };

        if (
          sectionKey.includes("address") ||
          sectionName.includes("address") ||
          mapsTo.includes("address")
        ) {
          addressCustom.push(customFieldObj);
        } else if (
          sectionKey.includes("customer") ||
          sectionKey.includes("client") ||
          sectionKey.includes("personal") ||
          sectionName.includes("customer") ||
          sectionName.includes("parent") ||
          sectionName.includes("personal") ||
          sectionKey === "customer_info" ||
          sectionKey === "other" ||
          !sectionKey
        ) {
          customerCustom.push(customFieldObj);
        } else {
          const sTitle = q.sectionName || q.section || "Additional Details";
          if (!otherSectionsMap.has(sTitle)) {
            otherSectionsMap.set(sTitle, []);
          }
          otherSectionsMap.get(sTitle).push(customFieldObj);
        }
      });

      const otherCustomSections = Array.from(otherSectionsMap.entries()).map(
        ([title, fields]) => ({ title, fields }),
      );

      setFormConfig({
        labelMap,
        customerCustomFields: customerCustom,
        addressCustomFields: addressCustom,
        otherCustomSections,
      });
    } catch (err) {
      console.error("Error loading dynamic form questions:", err);
    }
  }, [clientForm.intake_form_id]);

  useEffect(() => {
    if (isOpen) {
      fetchFormConfig();
    }
  }, [isOpen, fetchFormConfig]);

  useEffect(() => {
    if (isOpen && clientId) getClientDetails();
  }, [isOpen, clientId]);

  // Predefined label options with icons
  const labelOptions = [
    {
      value: "New Client",
      label: "🆕 New  - New Orders.",
    },
    { value: "Regular Client", label: "🔄 Regular - Active " },
    { value: "Frequent Visitor", label: "🔥 Frequent - Active and repeat" },
    { value: "VIP Client", label: "⭐ Top - High Spending" },

    {
      value: "Premium Client",
      label: "💎 Returning - Visited before but not frequent",
    },
  ];
  const [selectedLabels, setSelectedLabels] = useState([]);
  const [errors, setErrors] = useState({});
  const [checkedPhone, setCheckedPhone] = useState("");
  const [whatsappManuallyEdited, setWhatsappManuallyEdited] = useState(false);

  const getClientDetails = async () => {
    try {
      const response = await httpService.get(
        `/vendorclient/getvendorclient/${clientId}`,
      );
      const client = response.data;
      setClientForm({
        ...client,
        whatsapp_number: client.whatsapp_number || client.phone,
        alternate_phone: client.alternate_phone || "",
        id_proof: client.id_proof || null,
        documents: client.documents || [],
        custom_fields: client.custom_fields || {},
        labels: client.labels || [], // keep string for payload
        reference_source: client.reference_source || "",
      });

      const matched = labelOptions.filter((option) =>
        client.labels.includes(option.value),
      );
      setSelectedLabels(matched);
    } catch (error) {
      console.log("clientPet/getClientAPI", error);
    }
  };
  const checkClientExists = async (phoneNumber) => {
    try {
      const response = await httpService.get(
        `/vendorclient/getvendorclients?vendor_id=${vendorId}`,
      );

      const clients = response?.data || [];

      const existingClient = clients.find(
        (client) => client.phone === phoneNumber,
      );

      if (existingClient) {
        toast.info("Client already exists!", {
          position: "top-right",
        });
      }
    } catch (error) {
      console.error("Error checking client:", error);
    }
  };
  // Refs for scrolling to error fields
  const phoneRef = useRef(null);

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const resetForm = () => {
    setClientForm({
      firstname: "",
      lastname: "",
      nickname: "",
      phone: "",
      whatsapp_number: "",
      alternate_phone: "",
      email: "",
      address: {
        address1: "",
        address2: "",
        landmark: "",
        city: "",
        state: "",
        zip: "",
      },
      vendor_id: vendorId,
      labels: "",
      client_notes: "",
      reference_source: "",
      id_proof: null,
      documents: [],
      custom_fields: {},
    });
    setSelectedLabels([]);
    setErrors({});
  };

  const handleChange = useCallback(
    (field, value) => {
      if (field.startsWith("address.")) {
        const addressField = field.split(".")[1];
        setClientForm((prev) => ({
          ...prev,
          address: {
            ...prev.address,
            [addressField]: value,
          },
        }));
      } else {
        setClientForm((prev) => ({ ...prev, [field]: value }));
      }

      // Clear error when user starts typing
      if (errors[field]) {
        setErrors((prev) => ({ ...prev, [field]: "" }));
      }
    },
    [errors],
  );

  const handleLabelsChange = (newValue, actionMeta) => {
    if (actionMeta?.action === "select-option") {
      if (selectedLabels.length >= 1) {
        toast.error(" You can add only ONE label per client!", {
          position: "top-right",
          autoClose: 2000,
        });
        return;
      }
    }

    if (
      actionMeta?.action === "remove-value" ||
      actionMeta?.action === "pop-value"
    ) {
      setSelectedLabels(newValue || []);
      const valueArray = (newValue || []).map((item) => item.value);
      setClientForm((prev) => ({ ...prev, labels: valueArray }));
      return;
    }

    setSelectedLabels(newValue || []);
    const valueArray = (newValue || []).map((item) => item.value);
    setClientForm((prev) => ({ ...prev, labels: valueArray }));
  };

  const validateForm = () => {
    const newErrors = {};

    // Only Phone validation is mandatory
    if (!clientForm.phone.trim()) {
      newErrors.phone = "Phone number is required";
    } else if (!/^\d{10}$/.test(clientForm.phone.trim())) {
      newErrors.phone = "Phone number must be 10 digits";
    }

    // Email validation (if provided)
    const EMAIL_RE =
      /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.(com|in|org|net|co|info|io)$/i;

    if (clientForm.email && !EMAIL_RE.test(clientForm.email.trim())) {
      newErrors.email = "Enter a valid email like abc123@gmail.com";
    }

    // WhatsApp validation (if provided)
    if (
      clientForm.whatsapp_number &&
      !/^\d{10}$/.test(clientForm.whatsapp_number.trim())
    ) {
      newErrors.whatsapp_number = "WhatsApp number must be 10 digits";
    }

    // Alternate phone validation (if provided)
    if (
      clientForm.alternate_phone &&
      !/^\d{10}$/.test(clientForm.alternate_phone.trim())
    ) {
      newErrors.alternate_phone = "Alternate phone number must be 10 digits";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const scrollToFirstError = () => {
    if (errors.phone && phoneRef.current) {
      phoneRef.current.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
      phoneRef.current.focus();
    }
  };

  const handleSave = async () => {
    if (!validateForm()) {
      toast.warn("Please fill all required fields correctly!", {
        position: "top-right",
      });
      setTimeout(scrollToFirstError, 100);
      return;
    }

    try {
      // Prepare payload with notes in multiple possible field names for API compatibility
      const payload = {
        ...clientForm,
        client_name: clientForm.nickname,
        notes: clientForm.client_notes, // Add notes field as well in case API expects this
        whatsapp_number: clientForm.whatsapp_number || clientForm.phone,
        alternate_phone: clientForm.alternate_phone || null,
        custom_fields: clientForm.custom_fields || {},
      };

      console.log("📤 Sending client data to API:", payload);
      console.log("📝 Notes field value:", clientForm.client_notes);

      if (clientId) {
        const response = await httpService.put(
          `/vendorclient/updatevendorclient/${clientId}`,
          payload,
        );
        console.log("Client Updated: ", response);
        if (response) {
          toast.success("Client updated successfully!!", {
            position: "top-right",
          });
          await getClientDetails();
          onSave(response.data);
          handleClose();
        }
      } else {
        const response = await httpService.post(
          `/vendorclient/addvendorclient`,
          payload,
        );
        console.log("Client Added: ", response);
        if (response) {
          toast.success("Client added successfully!", {
            position: "top-right",
          });
          onSave(response.data);
          handleClose();
        }
      }
    } catch (error) {
      console.error("Error adding/updating client:", error);
    }
  };

  const handleSaveAndAddPet = async () => {
    if (!validateForm()) {
      toast.warn("Please fill all required fields correctly!", {
        position: "top-right",
      });
      setTimeout(scrollToFirstError, 100);
      return;
    }

    try {
      // Prepare payload with notes in multiple possible field names for API compatibility
      const payload = {
        ...clientForm,
        client_name: clientForm.nickname,
        notes: clientForm.client_notes, // Add notes field as well in case API expects this
        whatsapp_number: clientForm.whatsapp_number || clientForm.phone,
        alternate_phone: clientForm.alternate_phone || null,
        custom_fields: clientForm.custom_fields || {},
      };

      console.log("📤 Save & Add Pet - Sending client data to API:", payload);
      console.log("📝 Notes field value:", clientForm.client_notes);

      const response = await httpService.post(
        `/vendorclient/addvendorclient`,
        payload,
      );
      console.log("Client Added: ", response);
      if (response) {
        toast.success("Client added successfully! Opening pet form...", {
          position: "top-right",
        });
        // Call the onSaveAndAddPet callback if provided
        if (onSaveAndAddPet) {
          onSaveAndAddPet(response.data);
        } else {
          onSave(response.data);
        }
        handleClose();
      }
    } catch (error) {
      console.error("Error adding client:", error);
    }
  };

  const customSelectStyles = {
    control: (provided) => ({
      ...provided,
      minHeight: "40px",
      border: "1px solid #e5e7eb",
      borderRadius: "12px",
      boxShadow: "none",
      "&:hover": {
        border: "1px solid #3b82f6",
      },
      "&:focus": {
        border: "1px solid #3b82f6",
        boxShadow: "0 0 0 1px #3b82f6",
      },
    }),
    multiValue: (provided) => ({
      ...provided,
      backgroundColor: "#eff6ff",
      borderRadius: "8px",
      border: "1px solid #bfdbfe",
    }),
    multiValueLabel: (provided) => ({
      ...provided,
      color: "#1e40af",
      fontSize: "13px",
      fontWeight: "500",
    }),
    multiValueRemove: (provided) => ({
      ...provided,
      color: "#6b7280",
      "&:hover": {
        backgroundColor: "#fecaca",
        color: "#dc2626",
      },
    }),
  };

  // for validation - only allows numbers
  const handleInputChange = (e) => {
    const value = e.target.value;
    const numericValue = value.replace(/[^0-9]/g, "");

    if (numericValue.length <= 10) {
      setClientForm((prev) => {
        const updated = { ...prev, phone: numericValue };

        if (!whatsappManuallyEdited) {
          updated.whatsapp_number = numericValue;
        }

        return updated;
      });

      if (numericValue.length === 10 && numericValue !== checkedPhone) {
        setCheckedPhone(numericValue);
        checkClientExists(numericValue);
      }
    }
  };

  const getCustomFieldValue = (f) => {
    const custom = clientForm.custom_fields || {};
    const key = f.field_uid || f.id || f._id;
    if (custom[key] !== undefined && custom[key] !== null) return custom[key];
    if (f._id && custom[f._id] !== undefined && custom[f._id] !== null)
      return custom[f._id];
    if (f.label && custom[f.label] !== undefined && custom[f.label] !== null)
      return custom[f.label];
    return "";
  };

  const handleCustomFieldChange = (f, value) => {
    const key = f.field_uid || f.id || f._id || f.label;
    setClientForm((prev) => ({
      ...prev,
      custom_fields: {
        ...(prev.custom_fields || {}),
        [key]: value,
      },
    }));
  };

  const renderCustomField = (f) => {
    const value = getCustomFieldValue(f);
    const type = String(f.answerType || "").toLowerCase();
    const isRequired = f.isRequired;

    if (
      type === "dropdown" ||
      type === "select" ||
      type === "single_choice" ||
      type === "mcq" ||
      (Array.isArray(f.options) && f.options.length > 0)
    ) {
      return (
        <div key={f.field_uid || f._id || f.label}>
          <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
            <FiTag className="w-3 h-3 text-blue-600" />
            {f.label} {isRequired && <span className="text-red-500">*</span>}
          </label>
          <select
            value={value || ""}
            onChange={(e) => handleCustomFieldChange(f, e.target.value)}
            className="w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 text-sm bg-white"
          >
            <option value="">Select {f.label}</option>
            {(f.options || []).map((opt, idx) => {
              const optVal =
                typeof opt === "object"
                  ? opt.optionText || opt.value || opt.label
                  : opt;
              return (
                <option key={idx} value={optVal}>
                  {optVal}
                </option>
              );
            })}
          </select>
        </div>
      );
    }

    if (type === "long_text" || type === "textarea") {
      return (
        <div key={f.field_uid || f._id || f.label} className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
            <FiFileText className="w-3 h-3 text-blue-600" />
            {f.label} {isRequired && <span className="text-red-500">*</span>}
          </label>
          <textarea
            value={value || ""}
            onChange={(e) => handleCustomFieldChange(f, e.target.value)}
            rows={2}
            placeholder={`Enter ${f.label.toLowerCase()}`}
            className="w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 text-sm bg-white"
          />
        </div>
      );
    }

    if (type === "date" || type === "date_picker") {
      return (
        <div key={f.field_uid || f._id || f.label}>
          <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
            <FiEdit3 className="w-3 h-3 text-blue-600" />
            {f.label} {isRequired && <span className="text-red-500">*</span>}
          </label>
          <input
            type="date"
            value={value ? String(value).split("T")[0] : ""}
            onChange={(e) => handleCustomFieldChange(f, e.target.value)}
            className="w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 text-sm bg-white"
          />
        </div>
      );
    }

    const isPhone =
      type.includes("phone") ||
      f.label.toLowerCase().includes("phone") ||
      f.label.toLowerCase().includes("contact") ||
      f.label.toLowerCase().includes("mobile");

    return (
      <div key={f.field_uid || f._id || f.label}>
        <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
          {isPhone ? (
            <FiPhone className="w-3 h-3 text-green-600" />
          ) : (
            <FiEdit3 className="w-3 h-3 text-blue-600" />
          )}
          {f.label} {isRequired && <span className="text-red-500">*</span>}
        </label>
        <div className="relative">
          <input
            type={type === "number" ? "number" : "text"}
            value={value || ""}
            onChange={(e) => {
              let val = e.target.value;
              if (isPhone) {
                val = val.replace(/[^0-9]/g, "");
                if (val.length > 10) val = val.slice(0, 10);
              }
              handleCustomFieldChange(f, val);
            }}
            placeholder={`Enter ${f.label.toLowerCase()}`}
            className="w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 text-sm pl-10 bg-white"
          />
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            {isPhone ? (
              <FiPhone className="w-4 h-4 text-gray-400" />
            ) : (
              <FiEdit3 className="w-4 h-4 text-gray-400" />
            )}
          </div>
        </div>
      </div>
    );
  };

  const knownFieldUids = new Set([
    ...formConfig.customerCustomFields.map((f) => String(f.field_uid)),
    ...formConfig.addressCustomFields.map((f) => String(f.field_uid)),
    ...formConfig.customerCustomFields.map((f) => String(f.label).toLowerCase()),
    ...formConfig.addressCustomFields.map((f) => String(f.label).toLowerCase()),
    ...formConfig.otherCustomSections.flatMap((s) =>
      s.fields.map((f) => String(f.field_uid)),
    ),
  ]);

  const leftoverCustomKeys = Object.keys(clientForm.custom_fields || {}).filter(
    (k) =>
      !knownFieldUids.has(String(k)) &&
      !knownFieldUids.has(String(k).toLowerCase()) &&
      !isPetScopeField(k, k, k) &&
      clientForm.custom_fields[k] !== undefined &&
      clientForm.custom_fields[k] !== null &&
      clientForm.custom_fields[k] !== "",
  );

  const idProofDocs = useMemo(() => {
    const list = [];
    const seen = new Set();

    const p = clientForm?.id_proof;
    if (p) {
      const url = typeof p === "string" ? p : p.url;
      const name = typeof p === "object" && p.name ? p.name : "ID Proof Document";
      if (url && !seen.has(url)) {
        seen.add(url);
        list.push({
          name,
          url,
          type:
            typeof p === "object" && p.type
              ? p.type
              : url.match(/\.pdf($|\?)/i)
                ? "pdf"
                : "image",
        });
      }
    }

    const docs = Array.isArray(clientForm?.documents)
      ? clientForm.documents
      : [];
    for (const d of docs) {
      if (!d) continue;
      const url = typeof d === "string" ? d : d.url;
      if (!url || seen.has(url)) continue;
      const cat = String(d.category || "").toLowerCase();
      const name = String(d.name || "").toLowerCase();
      const isIdProof =
        cat === "id_proof" ||
        cat === "document" ||
        name.includes("id") ||
        name.includes("proof") ||
        name.includes("aadhaar") ||
        name.includes("pan") ||
        name.includes("license") ||
        name.includes("passport");

      if (isIdProof) {
        seen.add(url);
        list.push({
          name,
          url,
          type: d.type || (url.match(/\.pdf($|\?)/i) ? "pdf" : "image"),
        });
      }
    }

    return list;
  }, [clientForm.id_proof, clientForm.documents]);

  return (
    <div className="h-[calc(100vh-73px)] flex flex-col bg-gradient-to-b from-blue-50 to-white">
      {/* Main Form Content */}
      <div className="flex-1 p-3 overflow-y-auto space-y-2">
        {/* Personal Information & Contact Information Combined */}
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-3">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-8 h-8 bg-gradient-to-r from-green-100 to-emerald-100 rounded-full flex items-center justify-center">
              <FiUser className="w-4 h-4 text-green-600" />
            </div>
            <h4 className="text-base font-semibold text-gray-800">
              Personal Information
            </h4>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* First Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                <FiUser className="w-3 h-3 text-blue-600" />
                {formConfig.labelMap["customer.firstname"] || "First Name"}
              </label>
              <div className="relative">
                <input
                  type="text"
                  value={clientForm.firstname}
                  onChange={(e) => handleChange("firstname", e.target.value)}
                  placeholder={`Enter ${(formConfig.labelMap["customer.firstname"] || "first name").toLowerCase()}`}
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 text-sm pl-10 bg-white"
                />
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <FiEdit3 className="w-4 h-4 text-gray-400" />
                </div>
              </div>
            </div>
            {/* Last Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                <FiUser className="w-3 h-3 text-blue-600" />
                {formConfig.labelMap["customer.lastname"] || "Last Name"}
              </label>
              <div className="relative">
                <input
                  type="text"
                  value={clientForm.lastname}
                  onChange={(e) => handleChange("lastname", e.target.value)}
                  placeholder={`Enter ${(formConfig.labelMap["customer.lastname"] || "last name").toLowerCase()}`}
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 text-sm pl-10 bg-white"
                />
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <FiEdit3 className="w-4 h-4 text-gray-400" />
                </div>
              </div>
            </div>
            {/* Phone Number */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                <FiPhone className="w-3 h-3 text-green-600" />
                {formConfig.labelMap["customer.phone"] || "Phone Number"}{" "}
                <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <input
                  ref={phoneRef}
                  type="text"
                  value={clientForm.phone}
                  onChange={handleInputChange}
                  placeholder={`Enter ${(formConfig.labelMap["customer.phone"] || "phone number").toLowerCase()}`}
                  maxLength={10}
                  pattern="[0-9]*"
                  inputMode="numeric"
                  className={`w-full px-3 py-2.5 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 text-sm pl-10 ${
                    errors.phone
                      ? "border-red-500 bg-red-50"
                      : "border-gray-200 bg-white"
                  }`}
                />
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <FiPhone className="w-4 h-4 text-gray-400" />
                </div>
              </div>
              {errors.phone && (
                <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
                  <span className="w-3 h-3 bg-red-500 rounded-full text-white text-xs flex items-center justify-center">
                    !
                  </span>
                  {errors.phone}
                </p>
              )}
            </div>
            {/* WhatsApp Number */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                <FiMessageSquare className="w-3 h-3 text-green-600" />
                {formConfig.labelMap["customer.whatsapp_number"] ||
                  "WhatsApp Number"}
              </label>
              <div className="relative">
                <input
                  type="text"
                  value={clientForm.whatsapp_number}
                  onChange={(e) => {
                    const value = e.target.value.replace(/[^0-9]/g, "");
                    if (/^\d{0,10}$/.test(value)) {
                      handleChange("whatsapp_number", value);
                    }
                  }}
                  placeholder={`Enter ${(formConfig.labelMap["customer.whatsapp_number"] || "WhatsApp number").toLowerCase()}`}
                  className={`w-full px-3 py-2.5 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 text-sm pl-10 ${
                    errors.whatsapp_number
                      ? "border-red-500 bg-red-50"
                      : "border-gray-200 bg-white"
                  }`}
                />
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <FiMessageSquare className="w-4 h-4 text-gray-400" />
                </div>
              </div>
              {errors.whatsapp_number && (
                <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
                  <span className="w-3 h-3 bg-red-500 rounded-full text-white text-xs flex items-center justify-center">
                    !
                  </span>
                  {errors.whatsapp_number}
                </p>
              )}
            </div>
            {/* Alternate Phone Number */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                <FiPhone className="w-3 h-3 text-emerald-600" />
                {formConfig.labelMap["customer.alternate_phone"] ||
                  "Alternate Phone Number"}
              </label>
              <div className="relative">
                <input
                  type="text"
                  value={clientForm.alternate_phone || ""}
                  onChange={(e) => {
                    const value = e.target.value.replace(/[^0-9]/g, "");
                    if (/^\d{0,10}$/.test(value)) {
                      handleChange("alternate_phone", value);
                    }
                  }}
                  placeholder={`Enter ${(formConfig.labelMap["customer.alternate_phone"] || "alternate phone number").toLowerCase()}`}
                  maxLength={10}
                  pattern="[0-9]*"
                  inputMode="numeric"
                  className={`w-full px-3 py-2.5 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 text-sm pl-10 ${
                    errors.alternate_phone
                      ? "border-red-500 bg-red-50"
                      : "border-gray-200 bg-white"
                  }`}
                />
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <FiPhone className="w-4 h-4 text-gray-400" />
                </div>
              </div>
              {errors.alternate_phone && (
                <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
                  <span className="w-3 h-3 bg-red-500 rounded-full text-white text-xs flex items-center justify-center">
                    !
                  </span>
                  {errors.alternate_phone}
                </p>
              )}
            </div>
            {/* Email Address */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                <FiMail className="w-3 h-3 text-orange-600" />
                {formConfig.labelMap["customer.email"] || "Email Address"}
              </label>
              <div className="relative">
                <input
                  type="email"
                  value={clientForm.email}
                  onChange={(e) => {
                    let value = e.target.value.trimStart();
                    value = value.replace(/\.{2,}/g, ".");
                    value = value.replace(
                      /(\.(com|in|org|net|co|info|io)).*$/i,
                      "$1",
                    );
                    handleChange("email", value);
                  }}
                  placeholder={`Enter ${(formConfig.labelMap["customer.email"] || "email address").toLowerCase()}`}
                  className={`w-full px-3 py-2.5 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 text-sm pl-10 ${
                    errors.email
                      ? "border-red-500 bg-red-50"
                      : "border-gray-200 bg-white"
                  }`}
                />
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <FiMail className="w-4 h-4 text-gray-400" />
                </div>
              </div>
              {errors.email && (
                <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
                  <span className="w-3 h-3 bg-red-500 rounded-full text-white text-xs flex items-center justify-center">
                    !
                  </span>
                  {errors.email}
                </p>
              )}
            </div>
            {/* Reference */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                <GoPeople className="w-3 h-3 text-orange-600" />
                Reference
              </label>
              <select
                value={clientForm.reference_source || ""}
                onChange={(e) =>
                  handleChange("reference_source", e.target.value)
                }
                className="w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 text-sm bg-white"
              >
                <option value="">Select Reference</option>
                <option value="Friend">Friend</option>
                <option value="Findlocalpetservices.com">
                  Findlocalpetservices.com
                </option>
                <option value="Google">Google</option>
                <option value="Justdial">Justdial</option>
                <option value="Social Media">Social Media</option>
              </select>
            </div>
            {/* Dynamic Custom Fields in Personal Information */}
            {formConfig.customerCustomFields.map(renderCustomField)}
            {/* Any leftover stored custom fields for this client */}
            {leftoverCustomKeys.map((k) =>
              renderCustomField({
                field_uid: k,
                label: k
                  .replace(/[_-]/g, " ")
                  .replace(/\b\w/g, (c) => c.toUpperCase()),
                answerType: "text",
              }),
            )}
            {/* Uploaded ID Proof / Document */}
            {idProofDocs.length > 0 && (
              <div className="md:col-span-2 mt-2 p-3 bg-blue-50/70 border border-blue-200 rounded-xl flex items-center justify-between">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-9 h-9 bg-blue-100 rounded-lg flex items-center justify-center text-blue-600 shrink-0">
                    <FiFileText className="w-5 h-5" />
                  </div>
                  <div className="min-w-0">
                    <div className="text-[11px] font-semibold text-blue-800 uppercase tracking-wider">
                      {idProofDocs.length > 1
                        ? `ID Proofs / Documents (${idProofDocs.length})`
                        : "ID Proof / Document"}
                    </div>
                    <div className="text-sm font-medium text-gray-800 truncate max-w-xs sm:max-w-md">
                      {idProofDocs.length > 1
                        ? `${idProofDocs[0].name} (+${idProofDocs.length - 1} more)`
                        : idProofDocs[0].name}
                    </div>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setSelectedIdIndex(0);
                    setIsIdModalOpen(true);
                  }}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors shadow-sm cursor-pointer shrink-0"
                >
                  <FiFileText className="w-3.5 h-3.5" />
                  <span>
                    {idProofDocs.length > 1
                      ? `View Documents (${idProofDocs.length})`
                      : "View Document"}
                  </span>
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Address Information */}
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-3">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-8 h-8 bg-gradient-to-r from-red-100 to-orange-100 rounded-full flex items-center justify-center">
              <FiMapPin className="w-4 h-4 text-red-600" />
            </div>
            <h4 className="text-base font-semibold text-gray-800">
              Address Information
            </h4>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                <FiHome className="w-3 h-3 text-indigo-600" />
                {formConfig.labelMap["customer.address.address1"] ||
                  "Home Address"}
              </label>
              <div className="relative">
                <input
                  type="text"
                  value={clientForm?.address?.address1 || ""}
                  onChange={(e) =>
                    handleChange("address.address1", e.target.value)
                  }
                  placeholder={`Enter ${(formConfig.labelMap["customer.address.address1"] || "home address").toLowerCase()}`}
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 text-sm pl-10 bg-white"
                />
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <FiMapPin className="w-4 h-4 text-gray-400" />
                </div>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                <FiMapPin className="w-3 h-3 text-purple-600" />
                {formConfig.labelMap["customer.address.landmark"] || "Landmark"}
              </label>
              <input
                type="text"
                value={clientForm?.address?.landmark || ""}
                onChange={(e) =>
                  handleChange("address.landmark", e.target.value)
                }
                placeholder={`Enter ${(formConfig.labelMap["customer.address.landmark"] || "landmark").toLowerCase()}`}
                className="w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 text-sm bg-white"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                <FiMapPin className="w-3 h-3 text-blue-600" />
                {formConfig.labelMap["customer.address.city"] || "City"}
              </label>
              <input
                type="text"
                value={clientForm?.address?.city || ""}
                onChange={(e) => handleChange("address.city", e.target.value)}
                placeholder={`Enter ${(formConfig.labelMap["customer.address.city"] || "city").toLowerCase()}`}
                className="w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 text-sm bg-white"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                <FiMapPin className="w-3 h-3 text-green-600" />
                {formConfig.labelMap["customer.address.state"] || "State"}
              </label>
              <input
                type="text"
                value={clientForm?.address?.state || ""}
                onChange={(e) => {
                  const value = e.target.value;
                  // Allow only alphabets and space
                  if (/^[a-zA-Z\s]*$/.test(value)) {
                    handleChange("address.state", value);
                  }
                }}
                placeholder={`Enter ${(formConfig.labelMap["customer.address.state"] || "state").toLowerCase()}`}
                className="w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 text-sm bg-white"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                <FiMapPin className="w-3 h-3 text-orange-600" />
                {formConfig.labelMap["customer.address.zip"] || "Pincode"}
              </label>
              <input
                type="text"
                value={clientForm.address.zip || ""}
                onChange={(e) => {
                  const value = e.target.value;
                  // Allow only numbers and limit to 6 digits
                  if (/^\d{0,6}$/.test(value)) {
                    handleChange("address.zip", value);
                  }
                }}
                placeholder={`Enter ${(formConfig.labelMap["customer.address.zip"] || "pincode").toLowerCase()}`}
                maxLength={6}
                className={`w-full px-3 py-2.5 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 text-sm bg-white ${
                  clientForm.address.zip && clientForm.address.zip.length !== 6
                    ? "border-red-500"
                    : "border-gray-200"
                }`}
              />
              {/* Optional error message */}
              {clientForm.address.zip &&
                clientForm.address.zip.length !== 6 && (
                  <p className="text-xs text-red-500 mt-1">
                    Pincode must be 6 digits.
                  </p>
                )}
            </div>
            {/* Dynamic Custom Fields in Address Information */}
            {formConfig.addressCustomFields.map(renderCustomField)}
          </div>
        </div>

        {/* Dynamic Extra Sections (if configured) */}
        {formConfig.otherCustomSections.map((sec, sIdx) => (
          <div
            key={sIdx}
            className="bg-white rounded-xl border border-gray-100 shadow-sm p-3"
          >
            <div className="flex items-center gap-3 mb-3">
              <div className="w-8 h-8 bg-gradient-to-r from-purple-100 to-indigo-100 rounded-full flex items-center justify-center">
                <FiFileText className="w-4 h-4 text-purple-600" />
              </div>
              <h4 className="text-base font-semibold text-gray-800">
                {sec.title}
              </h4>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {sec.fields.map(renderCustomField)}
            </div>
          </div>
        ))}

        {/* Additional Information */}
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-3">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-8 h-8 bg-gradient-to-r from-teal-100 to-cyan-100 rounded-full flex items-center justify-center">
              <FiFileText className="w-4 h-4 text-teal-600" />
            </div>
            <h4 className="text-base font-semibold text-gray-800">
              Additional Information
            </h4>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
              <FiTag className="w-3 h-3 text-purple-600" />
              Labels
            </label>
            <Select
              isMulti
              value={selectedLabels}
              onChange={handleLabelsChange}
              options={labelOptions} // existing labels only
              styles={customSelectStyles}
              placeholder="Select existing labels..."
              className="text-sm"
              isOptionDisabled={() => selectedLabels.length >= 1}
              onMenuOpen={() => {
                if (selectedLabels.length >= 1) {
                  toast.info("You can add only ONE label per client!", {
                    position: "top-right",
                    autoClose: 2000,
                  });
                }
              }}
            />
          </div>

          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2 mt-2 flex items-center gap-2">
                <FiFileText className="w-3 h-3 text-blue-600" />
                Client Notes
              </label>
              <div className="relative">
                <textarea
                  value={clientForm?.client_notes || ""}
                  onChange={(e) => handleChange("client_notes", e.target.value)}
                  placeholder="Add any importantt notes about this client..."
                  rows={3}
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 text-sm pl-10 bg-white resize-none"
                />
                <div className="absolute top-2.5 left-0 pl-3 flex items-start pointer-events-none">
                  <FiEdit3 className="w-4 h-4 text-gray-400" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Fixed Footer with Action Buttons */}
      <div className="p-3 border-t border-gray-200 bg-gradient-to-r from-white to-blue-50 shadow-lg">
        <div className="flex gap-3">
          <button
            className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-gradient-to-r from-gray-100 to-gray-200 text-gray-700 rounded-lg font-medium hover:from-gray-200 hover:to-gray-300 transition-all duration-200 border border-gray-300"
            onClick={handleClose}
          >
            <FiX className="w-4 h-4" />
            Cancel
          </button>
          <button
            className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-lg font-medium hover:from-blue-600 hover:to-indigo-700 transition-all duration-200 shadow-md hover:shadow-lg transform hover:scale-105"
            onClick={handleSave}
          >
            <FiSave className="w-4 h-4" />
            {clientId ? `Update` : `Save`} Client
          </button>
          {!clientId && (
            <button
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-lg font-medium hover:from-green-600 hover:to-emerald-700 transition-all duration-200 shadow-md hover:shadow-lg transform hover:scale-105"
              onClick={handleSaveAndAddPet}
            >
              <FiUserPlus className="w-4 h-4" />
              Save & Add Pet
            </button>
          )}
        </div>
      </div>

      {/* ID Proof Gallery Preview Modal */}
      {isIdModalOpen && idProofDocs.length > 0 && (
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/80 backdrop-blur-xs p-4 animate-in fade-in duration-150"
          onClick={() => setIsIdModalOpen(false)}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full overflow-hidden flex flex-col max-h-[90vh] border border-gray-200"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gray-50">
              <div className="flex items-center gap-2.5 min-w-0">
                <div className="w-8 h-8 rounded-lg bg-blue-100 text-blue-700 flex items-center justify-center shrink-0">
                  <FiFileText className="w-4 h-4" />
                </div>
                <div className="min-w-0">
                  <h3 className="text-base font-bold text-gray-900 truncate max-w-md">
                    {idProofDocs[selectedIdIndex]?.name || "ID Proof Document"}
                  </h3>
                  {idProofDocs.length > 1 && (
                    <p className="text-xs text-gray-500 font-medium">
                      Document {selectedIdIndex + 1} of {idProofDocs.length}
                    </p>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <a
                  href={idProofDocs[selectedIdIndex]?.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-700 hover:text-blue-600 bg-white hover:bg-blue-50 border border-gray-300 rounded-lg transition-colors shadow-xs"
                  title="Open original in new tab"
                >
                  <FiExternalLink className="w-3.5 h-3.5" />
                  <span>Open Full</span>
                </a>
                <button
                  type="button"
                  onClick={() => setIsIdModalOpen(false)}
                  className="p-1.5 text-gray-400 hover:text-gray-700 hover:bg-gray-200 rounded-lg transition-colors cursor-pointer"
                  title="Close"
                >
                  <FiX className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Document Display Area */}
            <div className="relative flex-1 bg-gray-900 flex items-center justify-center p-4 min-h-[360px] max-h-[65vh] overflow-auto select-none">
              {idProofDocs[selectedIdIndex]?.url?.match(/\.pdf($|\?)/i) ||
              idProofDocs[selectedIdIndex]?.type === "pdf" ? (
                <iframe
                  src={idProofDocs[selectedIdIndex]?.url}
                  className="w-full h-full min-h-[480px] rounded-lg border-0 bg-white"
                  title="ID Proof Document"
                />
              ) : (
                <img
                  src={idProofDocs[selectedIdIndex]?.url}
                  alt={idProofDocs[selectedIdIndex]?.name || "ID Proof"}
                  className="max-h-[60vh] max-w-full object-contain rounded-md shadow-lg"
                />
              )}

              {/* Previous Button */}
              {idProofDocs.length > 1 && (
                <button
                  type="button"
                  onClick={() =>
                    setSelectedIdIndex((prev) =>
                      prev === 0 ? idProofDocs.length - 1 : prev - 1,
                    )
                  }
                  className="absolute left-3 top-1/2 -translate-y-1/2 p-2.5 bg-black/60 hover:bg-black/90 text-white rounded-full transition-all shadow-md cursor-pointer hover:scale-105 active:scale-95"
                  title="Previous Document"
                >
                  <FiChevronLeft className="w-5 h-5" />
                </button>
              )}

              {/* Next Button */}
              {idProofDocs.length > 1 && (
                <button
                  type="button"
                  onClick={() =>
                    setSelectedIdIndex((prev) =>
                      prev === idProofDocs.length - 1 ? 0 : prev + 1,
                    )
                  }
                  className="absolute right-3 top-1/2 -translate-y-1/2 p-2.5 bg-black/60 hover:bg-black/90 text-white rounded-full transition-all shadow-md cursor-pointer hover:scale-105 active:scale-95"
                  title="Next Document"
                >
                  <FiChevronRight className="w-5 h-5" />
                </button>
              )}
            </div>

            {/* Footer Thumbnail / Page Selector (when multiple documents exist) */}
            {idProofDocs.length > 1 && (
              <div className="px-6 py-3 bg-gray-50 border-t border-gray-200 flex items-center justify-center gap-2 overflow-x-auto">
                {idProofDocs.map((doc, idx) => (
                  <button
                    key={idx}
                    type="button"
                    onClick={() => setSelectedIdIndex(idx)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all cursor-pointer ${
                      selectedIdIndex === idx
                        ? "bg-blue-600 text-white shadow-sm ring-2 ring-blue-300"
                        : "bg-white text-gray-700 hover:bg-gray-100 border border-gray-200"
                    }`}
                  >
                    <FiFileText className="w-3.5 h-3.5" />
                    <span className="truncate max-w-[150px]">
                      {doc.name || `ID ${idx + 1}`}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AddNewClientAppointment;
